/**
 * สาขา / จุดเช็คอินเพิ่มเติม — HR เพิ่มเองได้ (วางลิงก์ Google Maps หรือพิกัด)
 * เช็คอินผ่านถ้าอยู่ในรัศมีของออฟฟิศหลัก "หรือ" สาขาใดสาขาหนึ่งที่เปิดใช้งาน.
 * ปิดใช้งานแทนการลบ — ประวัติเช็คอินเก่ายังอ้างชื่อสาขาได้.
 */
import { useState } from 'react';
import { Plus, MapPin, ExternalLink, Pencil, Link2 } from 'lucide-react';
import { useWorkSites, useSaveWorkSite, useResolveMapsLink, type WorkSite } from '../../hooks';
import { useToast } from '../../components/ui/toast';

type Draft = { id?: string; name: string; address: string; link: string; latitude: string; longitude: string; radius_m: string };
const EMPTY: Draft = { name: '', address: '', link: '', latitude: '', longitude: '', radius_m: '200' };

const mapsUrl = (lat: number | string, lng: number | string) => `https://www.google.com/maps?q=${lat},${lng}`;

export function WorkSitesCard() {
  const { data, isLoading, isError, refetch } = useWorkSites();
  const save = useSaveWorkSite();
  const resolve = useResolveMapsLink();
  const toast = useToast();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [geoLoading, setGeoLoading] = useState(false);

  const set = (k: keyof Draft, v: string) => setDraft((d) => (d ? { ...d, [k]: v } : d));

  function startEdit(s: WorkSite) {
    setDraft({
      id: s.id,
      name: s.name,
      address: s.address ?? '',
      link: '',
      latitude: String(s.latitude),
      longitude: String(s.longitude),
      radius_m: String(s.radius_m),
    });
  }

  async function handleResolve() {
    if (!draft?.link.trim()) {
      toast.error('วางลิงก์ Google Maps หรือพิกัดก่อน');
      return;
    }
    try {
      const c = await resolve.mutateAsync(draft.link.trim());
      setDraft((d) => (d ? { ...d, latitude: c.lat.toFixed(7), longitude: c.lng.toFixed(7) } : d));
      toast.success('ได้พิกัดแล้ว — กด "ดูบนแผนที่" เช็คว่าหมุดตรงสาขา');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'อ่านพิกัดไม่สำเร็จ');
    }
  }

  function handleUseCurrentLocation() {
    if (!navigator.geolocation) {
      toast.error('อุปกรณ์นี้ไม่รองรับการอ่านตำแหน่ง');
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setDraft((d) => (d ? { ...d, latitude: pos.coords.latitude.toFixed(7), longitude: pos.coords.longitude.toFixed(7) } : d));
        setGeoLoading(false);
      },
      () => {
        toast.error('อ่านตำแหน่งไม่สำเร็จ — เปิดสิทธิ์ตำแหน่งแล้วลองใหม่');
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  async function handleSave() {
    if (!draft) return;
    const lat = Number(draft.latitude);
    const lng = Number(draft.longitude);
    const radius = Number(draft.radius_m);
    if (!draft.name.trim()) return void toast.error('กรอกชื่อสาขา');
    if (draft.latitude === '' || draft.longitude === '' || !Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180 || (lat === 0 && lng === 0)) {
      return void toast.error('ยังไม่มีพิกัดที่ถูกต้อง — วางลิงก์แล้วกด "อ่านพิกัด" หรือกรอกเอง');
    }
    if (!Number.isInteger(radius) || radius < 20 || radius > 5000) return void toast.error('รัศมีต้องเป็นเลขเต็ม 20–5000 เมตร');
    try {
      await save.mutateAsync({ id: draft.id, name: draft.name.trim(), address: draft.address.trim(), latitude: lat, longitude: lng, radius_m: radius });
      toast.success(draft.id ? 'แก้ไขสาขาแล้ว' : 'เพิ่มสาขาแล้ว — พนักงานสแกนที่สาขานี้ได้ทันที');
      setDraft(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  async function toggleActive(s: WorkSite) {
    try {
      await save.mutateAsync({ id: s.id, active: !s.active });
      toast.success(s.active ? `ปิดใช้งาน ${s.name} แล้ว` : `เปิดใช้งาน ${s.name} แล้ว`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  const sites = data?.sites ?? [];

  return (
    <div className="card p-4 mb-3">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="font-semibold">สาขา / จุดเช็คอินเพิ่มเติม</div>
        {!draft && data?.ready !== false && (
          <button type="button" className="btn-secondary text-sm" onClick={() => setDraft({ ...EMPTY })}>
            <Plus className="w-4 h-4" /> เพิ่มสาขา
          </button>
        )}
      </div>
      <div className="text-xs text-gray-500 mb-3">
        พนักงานสแกนผ่านถ้าอยู่ในรัศมีของออฟฟิศหลัก หรือสาขาใดสาขาหนึ่งด้านล่าง
      </div>

      {isLoading && <div className="text-sm text-gray-500">กำลังโหลด…</div>}
      {isError && (
        <div className="text-sm text-red-600">
          โหลดรายการสาขาไม่สำเร็จ{' '}
          <button className="underline" onClick={() => refetch()}>ลองใหม่</button>
        </div>
      )}
      {data?.ready === false && (
        <div className="text-xs text-amber-700 bg-amber-50 rounded px-3 py-2">
          ระบบสาขายังไม่พร้อม — รอผู้ดูแลระบบอัปเดตฐานข้อมูล
        </div>
      )}
      {data?.ready && sites.length === 0 && !draft && (
        <div className="text-sm text-gray-500">ยังไม่มีสาขาเพิ่มเติม</div>
      )}

      <div className="space-y-2">
        {sites.map((s) => (
          <div key={s.id} className={`rounded-lg border p-3 ${s.active ? 'border-gray-200' : 'border-gray-100 bg-gray-50 opacity-70'}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium flex items-center gap-1">
                  <MapPin className="w-4 h-4 shrink-0 text-brand-600" />
                  <span className="truncate">{s.name}</span>
                  {!s.active && <span className="text-xs text-gray-500 whitespace-nowrap">(ปิดใช้งาน)</span>}
                </div>
                {s.address && <div className="text-xs text-gray-500 mt-0.5">{s.address}</div>}
                <div className="text-xs text-gray-600 mt-1 num">
                  {Number(s.latitude).toFixed(6)}, {Number(s.longitude).toFixed(6)} · รัศมี {s.radius_m} ม.
                </div>
                <a href={mapsUrl(s.latitude, s.longitude)} target="_blank" rel="noreferrer" className="text-xs text-brand-600 inline-flex items-center gap-1 mt-1">
                  <ExternalLink className="w-3 h-3" /> ดูบนแผนที่
                </a>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button type="button" className="btn-secondary text-xs" onClick={() => startEdit(s)} disabled={!!draft}>
                  <Pencil className="w-3 h-3" /> แก้ไข
                </button>
                <button type="button" className="btn-secondary text-xs" onClick={() => toggleActive(s)} disabled={save.isPending || !!draft}>
                  {s.active ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {draft && (
        <div className="mt-3 rounded-lg border border-brand-200 bg-brand-50/40 p-3 space-y-3">
          <div className="font-medium text-sm">{draft.id ? 'แก้ไขสาขา' : 'เพิ่มสาขาใหม่'}</div>
          <div>
            <label className="label">ชื่อสาขา</label>
            <input className="input" value={draft.name} onChange={(e) => set('name', e.target.value)} placeholder="เช่น สาขาเชียงราย" maxLength={100} />
          </div>
          <div>
            <label className="label">ลิงก์ Google Maps หรือพิกัด</label>
            <div className="flex gap-2">
              <input className="input flex-1 min-w-0" value={draft.link} onChange={(e) => set('link', e.target.value)} placeholder="https://maps.app.goo.gl/… หรือ 19.857671, 99.838813" />
              <button type="button" className="btn-secondary text-sm shrink-0" onClick={handleResolve} disabled={resolve.isPending}>
                <Link2 className="w-4 h-4" /> {resolve.isPending ? 'กำลังอ่าน…' : 'อ่านพิกัด'}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="min-w-0">
              <label className="label">Latitude</label>
              <input className="input num" inputMode="decimal" value={draft.latitude} onChange={(e) => set('latitude', e.target.value)} placeholder="19.857671" />
            </div>
            <div className="min-w-0">
              <label className="label">Longitude</label>
              <input className="input num" inputMode="decimal" value={draft.longitude} onChange={(e) => set('longitude', e.target.value)} placeholder="99.838813" />
            </div>
          </div>
          <div className="flex flex-wrap gap-3 text-xs">
            <button type="button" className="text-brand-600 inline-flex items-center gap-1" onClick={handleUseCurrentLocation} disabled={geoLoading}>
              <MapPin className="w-3 h-3" /> {geoLoading ? 'กำลังอ่านตำแหน่ง…' : 'ใช้ตำแหน่งปัจจุบัน (ยืนอยู่ที่สาขา)'}
            </button>
            {draft.latitude && draft.longitude && (
              <a href={mapsUrl(draft.latitude, draft.longitude)} target="_blank" rel="noreferrer" className="text-brand-600 inline-flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> ดูบนแผนที่
              </a>
            )}
          </div>
          <div>
            <label className="label">รัศมีที่อนุญาต (เมตร)</label>
            <input className="input num" type="number" min={20} max={5000} value={draft.radius_m} onChange={(e) => set('radius_m', e.target.value)} />
            <div className="text-xs text-gray-500 mt-1">แนะนำ 150–200 ม. ในห้าง/อาคาร เพราะ GPS ในร่มคลาดเคลื่อน</div>
          </div>
          <div>
            <label className="label">ที่อยู่ (ไม่บังคับ)</label>
            <input className="input" value={draft.address} onChange={(e) => set('address', e.target.value)} maxLength={300} />
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" className="btn-secondary text-sm" onClick={() => setDraft(null)} disabled={save.isPending}>
              ยกเลิก
            </button>
            <button type="button" className="btn-primary text-sm" onClick={handleSave} disabled={save.isPending}>
              {save.isPending ? 'กำลังบันทึก…' : draft.id ? 'บันทึกการแก้ไข' : 'เพิ่มสาขา'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
